import { range } from "lodash"
import { log } from "../../../../common/Logging"
import {
    ButtonContainer,
    Buttons,
    ButtonSize,
    ButtonType,
} from "../../../generic-sprites/Buttons"
import { Rectangle } from "../../../generic-sprites/Shape"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { VideoContainer, Videos } from "../../../utils/Videos"
import { MakeAVideoState } from "../MakeAVideoState"
import { MakeAVideoStore } from "../MakeAVideoStore"
import { MakeVideoImages } from "../MakeVideoAssets"
import { MakeVideoSprite, MakeVideoSpriteContainer } from "../MakeVideoTypes"
import { MakeVideoTimelineTile } from "./MakeVideoVideoTimeline"

export class MakeVideoClipPreviewContainer
    implements MakeVideoSprite, MakeVideoSpriteContainer
{
    active: boolean
    private clips: Array<MakeVideoClipPreview>
    private clearButton: ButtonContainer
    private video: VideoContainer
    private frame: Rectangle

    constructor(
        private game: Phaser.Game,
        group: Phaser.Group,
        x: number,
        y: number,
        private tiles: Array<MakeVideoTimelineTile>,
        private nextButton: ButtonContainer,
        private edit: boolean,
        debug: boolean,
        moveToStep: () => void,
    ) {
        this.frame = new Rectangle(
            game,
            Scale.x(0.07),
            Scale.y(0.4),
            Scale.x(0.3),
            Scale.y(0.3),
            { lineWidth: 1 },
        )
        this.game.world.sendToBack(this.frame.graphics)
        const clipIndices: Array<number> = range(1, 9)
        let currentX = x
        let currentY = y
        this.clips = clipIndices.map((index) => {
            const clip = new MakeVideoClipPreview(
                game,
                index,
                tiles,
                nextButton,
                this.previewOnClick,
                this.stopVideo,
                group,
                currentX,
                currentY,
                debug,
            )
            if (index === 4) {
                currentX = x
                currentY += clip.target.height + Scale.y(0.035)
            } else {
                currentX += clip.target.width + Scale.x(0.035)
            }
            return clip
        })

        if (edit) {
            MakeAVideoStore.instance.videos.forEach((video, index) => {
                const tile = this.tiles[index]
                const clip = this.clips.filter((c) => c.clip === video)[0]
                log.debug(
                    `In edit, tile index ${index} clip for video ${video} is null ? ${
                        clip == null
                    }`,
                )
                clip.centerOnTarget(tile.sprite.position)
                tile.addClip(clip.clip)
            })
        }

        this.clearButton = Buttons.create(game, "Clear All", this.clearAll, {
            x: Scale.x(0.654),
            y: Scale.y(0.926),
            buttonSize: ButtonSize.WIDE,
            buttonType: ButtonType.SECONDARY,
            debug,
        })

        this.active = true

        MakeAVideoState.debugPanel.setSkip(() => {
            this.game.sound.stopAll()
            moveToStep()
            clipIndices.forEach((index) => {
                const clip = MakeVideoClipPreview.videoClipName(index)
                log.debug(`With index ${index} found clip for video ${clip}`)
                MakeAVideoStore.instance.video = { index: index - 1, clip }
            })
            log.debug(
                `Videos is now ${JSON.stringify(
                    MakeAVideoStore.instance.videos,
                )}`,
            )
        })
    }

    clearAll = () => {
        this.clips.forEach((clip) => clip.returnToTarget())
        this.tiles.forEach((tile) => tile.removeClip())
        this.nextButton.disable()
    }

    destroy = () => {
        this.clips.forEach((clip) => clip.destroy())
        this.frame.destroy()
        this.clearButton.destroy()
    }

    disableAll = () => {
        this.clips.forEach((clip) => clip.disable())
        this.clearButton.disable()
    }

    enableAll = () => {
        this.clips.forEach((clip) => clip.enable())
        this.clearButton.enable()
        if (this.edit) {
            this.nextButton.enable()
        }
    }

    private previewOnClick = async (source: string) => {
        if (this.video && !this.video.destroyed) {
            this.video.destroy()
        }
        this.video = await Videos.add(this.game, source, {
            x: Scale.x(0.07),
            y: Scale.y(0.4),
            scale: 0.5,
        })
        this.video.sendToBack(this.game)
    }

    private stopVideo = () => {
        if (!this.video.destroyed) {
            this.video.destroy()
        }
    }
}

class MakeVideoClipPreview implements MakeVideoSprite {
    active: boolean
    target: Phaser.Sprite
    preview: Phaser.Sprite
    clip: string

    private static videoPreviewName = (v: number) => {
        if (v >= 1 && v <= 8) {
            return `video-preview-${v}.png`
        }
    }

    static videoClipName = (v: number) => {
        if (v >= 1 && v <= 8) {
            return `make-your-own-video-clip-${v}.mp4`
        }
    }

    constructor(
        private game: Phaser.Game,
        public index: number,
        private tiles: Array<MakeVideoTimelineTile>,
        private nextButton: ButtonContainer,
        private onClick: (str: string) => void,
        private stopVideo: () => void,
        group: Phaser.Group,
        x: number,
        y: number,
        debug: boolean,
    ) {
        this.target = Sprites.add(game, MakeVideoImages.VIDEO_TARGET, {
            scale: 0.88,
            debug,
            group,
            x,
            y,
        })

        this.preview = Sprites.add(
            game,
            MakeVideoClipPreview.videoPreviewName(index),
            { debug, group },
        )
        this.preview.width = this.target.width
        this.preview.height = this.target.height
        this.preview.position.copyFrom(this.target.position)
        this.preview.inputEnabled = true
        this.preview.input.enableDrag()
        this.preview.events.onDragStart.add(() => this.preview.bringToTop())
        this.preview.events.onDragStop.add(this.onPreviewDragStop)

        this.clip = MakeVideoClipPreview.videoClipName(index)
        this.enable()
    }

    enable = () =>
        Buttons.makeSpriteButton(this.preview, () => this.onClick(this.clip))

    disable = () => Buttons.undoSpriteButton(this.preview)

    destroy = () => {
        this.preview.events.destroy()
        this.preview.destroy(true)
        this.target.destroy(true)
    }

    returnToTarget = () => this.preview.position.copyFrom(this.target.position)

    centerOnTarget = (point: Phaser.Point) =>
        this.preview.position.copyFrom(point)

    private onPreviewDragStop = () => {
        const overlap = this.tiles.filter(
            (tile) =>
                !tile.filled && Sprites.checkOverlap(this.preview, tile.sprite),
        )
        switch (true) {
            case overlap.length === 1:
                this.stopVideo()
                overlap[0].addClip(this.clip)
                this.centerOnTarget(overlap[0].sprite.position)
                this.clearClipFromTile(overlap[0].tile)
                break
            case overlap.length > 1:
                this.stopVideo()
                const min = this.getClosestTileIndex(overlap)
                overlap[min].addClip(this.clip)
                this.centerOnTarget(overlap[min].sprite.position)
                this.clearClipFromTile(overlap[min].tile)
                break
            default:
                this.returnToTarget()
                this.clearClipFromTile()
        }
        MakeAVideoStore.instance.videos.every((video) => video !== "")
            ? this.nextButton.enable()
            : this.nextButton.disable()
    }

    private getClosestTileIndex = (
        overlap: Array<MakeVideoTimelineTile>,
    ): number =>
        overlap
            .map((tile) => Math.abs(tile.sprite.x - this.preview.x))
            .reduce((m, x, i, arr) => (x < arr[m] ? i : m), 0)

    private clearClipFromTile = (index?: number) => {
        const arr = index
            ? this.tiles.filter((tile) => tile.tile !== index)
            : this.tiles
        arr.forEach((tile) => {
            if (tile.clip === this.clip) {
                tile.removeClip()
            }
        })
    }
}
